import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import enhancedFinancialDataService from '../services/EnhancedFinancialDataService';

// Per-ticker overrides for the rare cases where TradingView needs an explicit
// exchange prefix (e.g. a ticker that is ambiguous across exchanges).
// Leave this map empty unless a specific ticker is confirmed to need it.
const TRADINGVIEW_TICKER_OVERRIDES = {
  // example: BMNR: 'NYSE:BMNR',
};

const getTradingViewExchangePrefix = (exchange) => {
  const raw = String(exchange || '').trim().toUpperCase();
  if (!raw) return '';

  if (raw.includes('NASDAQ')) return 'NASDAQ';
  if (raw.includes('NYSE ARCA') || raw.includes('ARCA')) return 'AMEX';
  if (raw.includes('NYSE MKT') || raw.includes('AMERICAN') || raw.includes('AMEX')) return 'AMEX';
  if (raw.includes('NYSE')) return 'NYSE';
  if (raw.includes('OTC')) return 'OTC';
  if (raw.includes('TSX')) return 'TSX';
  if (raw.includes('TSXV')) return 'TSXV';
  if (raw.includes('LSE')) return 'LSE';
  if (raw.includes('ASX')) return 'ASX';
  if (raw.includes('HKEX')) return 'HKEX';
  if (raw.includes('NSE')) return 'NSE';
  if (raw.includes('BSE')) return 'BSE';

  return '';
};

/**
 * Build a TradingView symbol string that keeps chart market in sync with the
 * selected exchange shown in the dashboard quote cards.
 */
const buildTradingViewSymbol = (exchange, ticker) => {
  const normalizedTicker = String(ticker || '').trim().toUpperCase();
  if (!normalizedTicker) return '';
  if (normalizedTicker.includes(':')) return normalizedTicker;

  if (TRADINGVIEW_TICKER_OVERRIDES[normalizedTicker]) {
    return TRADINGVIEW_TICKER_OVERRIDES[normalizedTicker];
  }

  const prefix = getTradingViewExchangePrefix(exchange);
  return prefix ? `${prefix}:${normalizedTicker}` : normalizedTicker;
};

const TradingView = ({ ticker, exchange, showFinancialBars = true }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const containerRef = useRef(null);
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredPeriod, setHoveredPeriod] = useState(null);
  const [financialPeriodType, setFinancialPeriodType] = useState('quarterly');

  // Sort data by period in ascending order (oldest to newest)
  const sortByPeriodAscending = (data) => {
    if (!data) return [];
    return [...data].sort((a, b) => new Date(a.period) - new Date(b.period));
  };

  const fetchFinancialData = async () => {
    if (!ticker) return;
    
    setLoading(true);
    setError(null);
    try {
      // Use the financial data service to get the data
      const response = await enhancedFinancialDataService.getFinancialData(ticker, false, financialPeriodType);
      
      if (response && Array.isArray(response) && response.length > 0) {
        console.log('Fetched financial data for chart:', response);
        
        // Sort data by period in ascending order (oldest to newest)
        const sortedData = sortByPeriodAscending(response);
        setFinancialData(sortedData);
        
        // Set initial hovered period to most recent period (last item after sorting)
        const mostRecentPeriod = sortedData[sortedData.length - 1].period;
        setHoveredPeriod(mostRecentPeriod);
      } else {
        console.warn('API returned empty or invalid data');
        setError("API returned empty or invalid data. Please try again later.");
        setFinancialData([]);
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
      setError("Failed to fetch financial data. Please try again later.");
      setFinancialData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBarHover = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const period = data.activePayload[0].payload.period;
      setHoveredPeriod(period);
    }
  };

  // Get data range for any data key to properly handle negative values
  const getDataRange = (data, key) => {
    if (!data || data.length === 0) return { min: 0, max: 0 };
    
    const values = data.map(item => Number(item[key]) || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Handle different scenarios for better scaling
    if (min >= 0) {
      // All positive values - start at 0
      return { min: 0, max: max * 1.1 }; // Add 10% padding at top
    } else {
      // Have negative values - need to handle specially
      return { 
        min: min * 1.1, // Add 10% padding below lowest negative value
        max: max > 0 ? max * 1.1 : 0 // Add padding to positive max or use 0 if all negative
      };
    }
  };

  useEffect(() => {
    // Clear previous chart if it exists
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      const tradingViewTheme = isDarkMode ? 'dark' : 'light';
      const normalizedTicker = (ticker || '').toUpperCase();
      const tvSymbol = buildTradingViewSymbol(exchange, normalizedTicker);
      const widgetScript = document.createElement('script');
      widgetScript.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      widgetScript.type = 'text/javascript';
      widgetScript.async = true;
      widgetScript.text = JSON.stringify({
        autosize: true,
        symbol: tvSymbol,
        interval: 'D',
        timezone: 'America/New_York',
        theme: tradingViewTheme,
        style: '1',
        locale: 'en',
        allow_symbol_change: false,
        hide_side_toolbar: true,
        hide_top_toolbar: false,
        hide_legend: false,
        hide_volume: false,
        withdateranges: true,
        calendar: true,
        details: true,
        hotlist: false,
        watchlist: [],
        compareSymbols: [],
        studies: ['Earnings@tv-basicstudies'],
        support_host: 'https://www.tradingview.com'
      });
      containerRef.current.appendChild(widgetScript);
    }
    
    // Fetch financial data only when the lower financial chart is shown
    if (showFinancialBars) {
      fetchFinancialData();
    }
    
    // Cleanup function
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [ticker, exchange, isDarkMode, financialPeriodType]); // Re-run when ticker, exchange, theme, or cadence changes

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: showFinancialBars ? 3 : 0 }}>
      {/* Trading View Chart */}
      <Box sx={{ width: '100%', height: { xs: '50vh', sm: 'calc(100vh - 350px)' }, minHeight: { xs: 320, sm: 420 } }}>
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          <div
            id="tradingview_widget_container"
            ref={containerRef}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Box>

      {showFinancialBars && (
        <Box sx={{ width: '100%', height: { xs: 340, sm: 400 } }}>
          <Paper sx={{ p: 2, height: '100%' }} elevation={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 0 }}>
                Revenue and Operating Income {ticker ? `for ${ticker}` : ''}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={financialPeriodType}
                onChange={(_event, nextType) => {
                  if (nextType) setFinancialPeriodType(nextType);
                }}
              >
                <ToggleButton value="quarterly">Quarterly</ToggleButton>
                <ToggleButton value="annual">Annual</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading financial data...</Typography>
              </Box>
            ) : financialData && financialData.length > 0 ? (
              <Box sx={{ height: '85%', width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={financialData}
                    margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: isMobile ? 12 : 5 }}
                    onMouseMove={handleBarHover}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="period"
                      angle={isMobile ? -35 : -45}
                      textAnchor="end"
                      height={isMobile ? 64 : 80}
                      tick={{ fontSize: isMobile ? 10 : 12, fill: theme.palette.text.secondary }}
                      tickFormatter={(value) => {
                        // Remove time portion from date string
                        return value.split('T')[0];
                      }}
                    />
                    <YAxis
                      tickFormatter={(value) => {
                        // Format based on value magnitude
                        if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
                        if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
                        if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
                        return `$${value.toFixed(1)}`;
                      }}
                      label={isMobile ? undefined : { value: 'Value ($)', angle: -90, position: 'insideLeft' }}
                      tickCount={7}
                      width={isMobile ? 56 : 72}
                      domain={[
                        Math.min(
                          getDataRange(financialData, 'Revenue').min,
                          getDataRange(financialData, 'Operating Income').min
                        ),
                        Math.max(
                          getDataRange(financialData, 'Revenue').max,
                          getDataRange(financialData, 'Operating Income').max
                        )
                      ]}
                    />
                    <Tooltip
                      formatter={(value, name) => {
                        // Format based on value magnitude
                        if (Math.abs(value) >= 1e9) return [`$${(value / 1e9).toFixed(2)}B`, name];
                        if (Math.abs(value) >= 1e6) return [`$${(value / 1e6).toFixed(2)}M`, name];
                        if (Math.abs(value) >= 1e3) return [`$${(value / 1e3).toFixed(2)}K`, name];
                        return [`$${value.toFixed(2)}`, name];
                      }}
                      labelFormatter={(label) => `Period: ${label}`}
                      contentStyle={{
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.96)' : '#ffffff',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                      labelStyle={{
                        color: theme.palette.mode === 'dark' ? '#f3f4f6' : '#111827',
                        fontWeight: 600,
                      }}
                      itemStyle={{
                        color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#1f2937',
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 10, fontSize: isMobile ? '0.8rem' : '0.9rem' }} />
                    <Bar
                      name="Revenue"
                      dataKey="Revenue"
                      fill="#8884d8"
                      fillOpacity={0.8}
                      barSize={40}
                    />
                    <Bar
                      name="Operating Income"
                      dataKey="Operating Income"
                      fill="#82ca9d"
                      fillOpacity={0.8}
                      barSize={40}
                    >
                      {financialData.map((entry, index) => {
                        // Change negative values to red
                        const operatingIncome = Number(entry['Operating Income']) || 0;
                        const color = operatingIncome < 0 ? '#ef4444' : '#82ca9d';
                        return <Cell key={`operating-income-cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                <Typography variant="h6" color="text.secondary">
                  No financial data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default TradingView;